import { Injectable, signal } from "@angular/core";
import { deriveSubAgentLabel } from "../model/block.types";
import type { SubAgentTodo } from "../model/block.types";

/** Raw `*_todo_update` custom-event payload sent by the backend (see `isTodoUpdateEvent`). */
export interface WorkerTodoEvent {
  todos?: SubAgentTodo[];
}

/** Raw `*_tool_call` custom-event payload (see `isSubAgentToolCallEvent`) — a sub-agent tool call that already completed. */
export interface WorkerToolCallEvent {
  tool_name?: string;
  label?: string;
}

/** Last completed tool call for the current turn's sub-agent — ephemeral, replaced wholesale by the next call. */
export interface WorkerToolCall {
  name: string;
  label: string;
}

/**
 * Holds the current turn's live TODO checklist and latest tool-call activity, streamed as
 * `*_todo_update`/`*_tool_call` SSE custom events (`deep_analysis_todo_update`/`_tool_call`,
 * `style_todo_update`/`_tool_call`, ...) for a sub-agent running as part of the MAIN chat turn —
 * i.e. NOT wrapped in a `background_job` block. `BackgroundJobTrackerService` owns the equivalent
 * state for pipeline jobs (`subAgentTodos` + `deepAnalysis.toolCall`), keyed by job_id; this
 * service is the counterpart for a plain conversational turn, which has no job_id to key by.
 *
 * Mirrors `WorkerReasoningStreamService`: `chat-container` routes matching custom events here,
 * `chat-message-list` weaves the current list into the turn as a `TodoListCardComponent`, which
 * renders the tool-call activity row above the checklist — same layout as
 * `TaskListCardComponent`'s pipeline equivalent. Payloads carry no run/task id of their own —
 * routing is implicit via which SSE stream delivered them — so a single "current" state is
 * enough (replaced wholesale on every event, same as the backend's own semantics).
 *
 * Provided per chat-container (NOT root) so it resets with the container, same lifecycle as
 * `WorkerReasoningStreamService`.
 */
@Injectable()
export class WorkerTodoStreamService {
  readonly #todos = signal<SubAgentTodo[]>([]);

  readonly #toolCall = signal<WorkerToolCall | null>(null);

  readonly #label = signal<string | null>(null);

  /** Live todo checklist for the current turn. Reactive — read in computed()s. */
  readonly todos = this.#todos.asReadonly();

  /** Last completed tool call for the current turn, or null if none yet. Reactive. */
  readonly toolCall = this.#toolCall.asReadonly();

  /** Sub-agent name derived from the event name (see `deriveSubAgentLabel`), e.g. "Style". Reactive. */
  readonly label = this.#label.asReadonly();

  /** Replace the whole list from a `*_todo_update` event — backend always sends the full list. */
  store(event: WorkerTodoEvent, eventName: string): void {
    this.#todos.set(event?.todos ?? []);
    this.#label.set(deriveSubAgentLabel(eventName));
  }

  /** Replace the current tool call from a `*_tool_call` event (see `isSubAgentToolCallEvent`). */
  storeToolCall(event: WorkerToolCallEvent, eventName: string): void {
    this.#toolCall.set({ name: event?.tool_name ?? "", label: event?.label || event?.tool_name || "Working…" });
    this.#label.set(deriveSubAgentLabel(eventName));
  }

  /** Drop the list, tool call and label. Call on submit so a new turn starts clean. */
  clear(): void {
    this.#todos.set([]);
    this.#toolCall.set(null);
    this.#label.set(null);
  }
}
